import {
    ADMIN,
    HUMAN_RESOURCES,
    HUMAN_RESOURCES_USER,
    SUPPORT,
} from 'business/constants';
import {
    MODULES_TRANSLATION_PATH,
    modulesWithSubRoles,
    modulesWithSubRolesWhenEssential,
} from './helpers/constants';
import React, { useEffect } from 'react';
import { UserRole, UserT } from 'business/definitions';
import { first, some } from 'lodash';
import {
    useClientHasModule,
    useIsEssential,
    useUserHasRole,
} from 'hooks/client';
import {
    useCreateMultipleUserContext,
    useCreateMultipleUserDispatchContext,
} from 'routes/Admin/UserManagement/Users/CreateMultipleUser/context/CreateMultipleUserContext';
import {
    useUserCreationFormContext,
    useUserCreationFormDispatchContext,
} from '../../../CreateUserContext/CreateUserFormContext';

import AuthSection from './AuthSection';
import { CreateMultipleUserActionType } from 'routes/Admin/UserManagement/Users/CreateMultipleUser/context/types';
import GenericSwitchBar from './ui/GenericSwitchBar';
import ModuleWithSubRoles from './ModulesWithSubRoles';
import SupportSection from './SupportSection';
import { USER_CREATION_FORM_ACTION_TYPE } from '../../../CreateUserContext/types';
import { isEmailDomainSupportWhitelisted } from '../../../helpers/email';
import { useTranslation } from 'react-i18next';

type Props = {
    users: Partial<UserT>[];
    roles: UserRole[];
    addRole: (role: UserRole) => void;
    removeRole: (role: UserRole) => void;
    changeAuthMethod: (auth: string) => void;
};

const RolesAccessSection = ({
    users,
    roles,
    addRole,
    removeRole,
    changeAuthMethod,
}: Props): JSX.Element => {
    const { t } = useTranslation();

    const hasHumanResources = useClientHasModule(HUMAN_RESOURCES);
    const isSupportUser = useUserHasRole(SUPPORT);
    const isEssential = useIsEssential();

    const isCreatedUserAdmin = roles.includes(ADMIN);
    const isCreatedUserHumanResourcesUser =
        roles.includes(HUMAN_RESOURCES_USER);

    const supportUsersEmail = users
        .filter((user) => user.roles?.includes(SUPPORT))
        .map((user) => user.email)
        .filter((v) => v !== undefined) as string[];

    const authMethods = users
        .flatMap((u) => u.authMethods)
        .filter((u) => u !== undefined) as string[];

    const handleSwitchChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        role: UserRole,
    ) => {
        if (event.target.checked) {
            addRole(role);
        } else {
            removeRole(role);
        }
    };

    return (
        <>
            <GenericSwitchBar
                checked={isCreatedUserAdmin}
                onChange={(event) => handleSwitchChange(event, ADMIN)}
                title={t(`${MODULES_TRANSLATION_PATH}.titles.${ADMIN}`)}
                tooltipContent={t(
                    `${MODULES_TRANSLATION_PATH}.descriptions.${ADMIN}`,
                )}
            />
            {isSupportUser && (
                <>
                    <SupportSection
                        addRole={addRole}
                        removeRole={removeRole}
                        roles={roles}
                        supportUsersEmail={supportUsersEmail}
                    />
                    <AuthSection
                        changeAuthMethod={changeAuthMethod}
                        authMethods={authMethods}
                    />
                </>
            )}
            {(isEssential
                ? modulesWithSubRolesWhenEssential
                : modulesWithSubRoles
            ).map((module) => (
                <ModuleWithSubRoles
                    addRole={addRole}
                    removeRole={removeRole}
                    roles={roles}
                    data-testid={`${module.name}-module-access`}
                    key={`${module.name}-module-access`}
                    module={module}
                />
            ))}
            {hasHumanResources && (
                <GenericSwitchBar
                    checked={isCreatedUserHumanResourcesUser}
                    onChange={(event) =>
                        handleSwitchChange(event, HUMAN_RESOURCES_USER)
                    }
                    title={t(
                        `${MODULES_TRANSLATION_PATH}.titles.${HUMAN_RESOURCES_USER}`,
                    )}
                    tooltipContent={t(
                        `${MODULES_TRANSLATION_PATH}.descriptions.${HUMAN_RESOURCES_USER}`,
                    )}
                />
            )}
        </>
    );
};

const SingleUserRolesAccessSection = (): JSX.Element => {
    const user = useUserCreationFormContext();
    const { dispatchUserCreationForm } = useUserCreationFormDispatchContext();
    const addRole = (role: UserRole) =>
        dispatchUserCreationForm({
            type: USER_CREATION_FORM_ACTION_TYPE.addRole,
            role,
        });
    const removeRole = (role: UserRole) =>
        dispatchUserCreationForm({
            type: USER_CREATION_FORM_ACTION_TYPE.removeRole,
            role,
        });
    const changeAuthMethod = (authMethod: string) =>
        dispatchUserCreationForm({
            type: USER_CREATION_FORM_ACTION_TYPE.changeAuthMethod,
            authMethod,
        });
    return (
        <RolesAccessSection
            users={[user]}
            roles={user.roles}
            addRole={addRole}
            removeRole={removeRole}
            changeAuthMethod={changeAuthMethod}
        />
    );
};

const MultipleUsersRolesAccessSection = ({
    setNextStepEnable,
}: {
    setNextStepEnable: (enable: boolean) => void;
}): JSX.Element => {
    const { users } = useCreateMultipleUserContext();
    const dispatch = useCreateMultipleUserDispatchContext();

    const roles = (first(users) as UserT | undefined)?.roles || [];

    useEffect(() => {
        if (
            roles.includes(SUPPORT) &&
            some(users, (u) => !isEmailDomainSupportWhitelisted(u.email))
        ) {
            setNextStepEnable(false);
        } else {
            setNextStepEnable(true);
        }
    }, [users, roles, setNextStepEnable]);

    const addRole = (role: UserRole) =>
        dispatch({ type: CreateMultipleUserActionType.addRole, role });

    const removeRole = (role: UserRole) =>
        dispatch({ type: CreateMultipleUserActionType.removeRole, role });

    const changeAuthMethod = (authMethod: string) =>
        dispatch({
            type: CreateMultipleUserActionType.changeAuthMethod,
            authMethod,
        });

    return (
        <RolesAccessSection
            users={users}
            roles={roles}
            addRole={addRole}
            removeRole={removeRole}
            changeAuthMethod={changeAuthMethod}
        />
    );
};

export { SingleUserRolesAccessSection, MultipleUsersRolesAccessSection };
